// @services/stubs/StubDocumentAssistantDictationClient.ts
import type { TaskEitherTs } from "@ps-bridge/interop";
import type { DocumentAssistantDictationClient } from "@services/clients/DocumentAssistantDictationClient";
import {
    decodeDictationTranscriptionResponse,
    dictationError,
    type DictationError,
    type DictationErrorCode,
    type DictationPayloadObject,
    type DictationTranscriptionRequest,
    type DictationTranscriptionResponse,
} from "@services/clients/DocumentAssistantDictationApi";
import { taskEitherLeft, taskEitherOf } from "@utils/effects/branded";
import * as Option from "@utils/Option";

export type StubDocumentAssistantDictationMode =
    | "success"
    | "permissionDenied"
    | "noMicrophone"
    | "unsupportedBrowser"
    | "emptyRecording"
    | "authRequired"
    | "uploadFailed"
    | "networkError"
    | "transcriptionUnavailable"
    | "responseDecodeError";

export const STUB_DOCUMENT_ASSISTANT_DICTATION_MODE_KEY = "stub:document-assistant-dictation:mode";
export const STUB_DOCUMENT_ASSISTANT_DICTATION_TRANSCRIPT_KEY = "stub:document-assistant-dictation:transcript";

const browserStorage = () =>
    typeof window === "undefined" ? Option.none<Storage>() : Option.some(window.localStorage);

const readStorageValue = (key: string): string =>
    Option.fold(browserStorage(), () => "", (storage) => storage.getItem(key) ?? "");

export const decodeStubDocumentAssistantDictationMode = (
    value: string,
): StubDocumentAssistantDictationMode => {
    switch (value) {
        case "permissionDenied":
        case "noMicrophone":
        case "unsupportedBrowser":
        case "emptyRecording":
        case "authRequired":
        case "uploadFailed":
        case "networkError":
        case "transcriptionUnavailable":
        case "responseDecodeError":
            return value;
        default:
            return "success";
    }
};

const readMode = (): StubDocumentAssistantDictationMode =>
    decodeStubDocumentAssistantDictationMode(readStorageValue(STUB_DOCUMENT_ASSISTANT_DICTATION_MODE_KEY));

const errorMessageFor = (code: Exclude<StubDocumentAssistantDictationMode, "success">): string => {
    switch (code) {
        case "permissionDenied":
            return "Microphone permission was denied.";
        case "noMicrophone":
            return "No microphone is available.";
        case "unsupportedBrowser":
            return "This browser does not support dictation recording.";
        case "emptyRecording":
            return "The recording did not contain audio.";
        case "authRequired":
            return "Sign in again before using dictation.";
        case "uploadFailed":
            return "The dictation upload failed.";
        case "networkError":
            return "The dictation network request failed.";
        case "transcriptionUnavailable":
            return "The transcription service is unavailable.";
        case "responseDecodeError":
            return "Unable to decode the transcription response.";
    }
};

const modeError = (
    mode: Exclude<StubDocumentAssistantDictationMode, "success">,
    request: DictationTranscriptionRequest,
): DictationError => {
    const code: DictationErrorCode = mode;
    return dictationError(code, errorMessageFor(mode), Option.some(request.capture.fieldId), Option.none());
};

const successPayload = (request: DictationTranscriptionRequest): DictationPayloadObject => ({
    transcript: readStorageValue(STUB_DOCUMENT_ASSISTANT_DICTATION_TRANSCRIPT_KEY) || "Stub dictation transcript.",
    traceId: `stub-dictation-trace-${request.capture.fieldId}`,
});

export class StubDocumentAssistantDictationClient implements DocumentAssistantDictationClient {
    transcribeAudio(
        request: DictationTranscriptionRequest,
    ): TaskEitherTs<DictationError, DictationTranscriptionResponse> {
        const mode = readMode();
        if (mode !== "success") {
            return taskEitherLeft(modeError(mode, request));
        }

        const decoded = decodeDictationTranscriptionResponse(successPayload(request));
        if (decoded.type === "error") {
            return taskEitherLeft(
                dictationError("responseDecodeError", decoded.message, Option.some(request.capture.fieldId), Option.none()),
            );
        }

        return taskEitherOf(decoded.value) as TaskEitherTs<DictationError, DictationTranscriptionResponse>;
    }
}
